import React from "react";
import { Grid, Typography, Box } from "@mui/material";
import { Step } from "../types";
import { StepperContainer, StepItem } from "../styled";

interface StepperHeaderProps {
  currentStep: Step;
  handleBack?: () => void;
  selectedMatchingRoundName?: string;
}

export const StepperHeader: React.FC<StepperHeaderProps> = ({
  currentStep,
  handleBack,
  selectedMatchingRoundName = ""
}) => {
  const renderStepper = () => {
    const steps = [
      { id: Step.SELECT_MATCHING_ROUND, label: "Select Round" },
      { id: Step.REVIEW, label: "Review" },
      { id: Step.CONFIRM_EXPORT, label: "Export" },
      { id: Step.EXPORT_LOG, label: "View Log" }
    ];

    // Helper function to determine if a step should be active
    const isStepActive = (stepId: Step) => {
      if (currentStep === stepId) return true;

      if (currentStep === Step.EXPORT_SUMMARY) {
        // When viewing export summary/details, treat it as the Export Log step
        return stepId === Step.EXPORT_LOG;
      }

      return false;
    };

    // Helper function to determine if a step should be completed
    const isStepCompleted = (stepId: Step) => {
      if (currentStep > stepId) return true;

      if (currentStep === Step.EXPORT_SUMMARY) {
        // When viewing export summary/details, show all steps before Export Log as completed
        return stepId < Step.EXPORT_LOG;
      }

      return false;
    };

    // Calculate how much of the progress line should be blue
    const getProgressPercentage = () => {
      const activeStepIndex = steps.findIndex(step => isStepActive(step.id));
      const completedSteps = steps.filter(step => isStepCompleted(step.id)).length;

      if (activeStepIndex >= 0) {
        // If there's an active step, progress up to that step
        return ((activeStepIndex) / (steps.length - 1)) * 100;
      } else if (completedSteps > 0) {
        // If there are completed steps but no active step, progress up to the last completed step
        return ((completedSteps - 1) / (steps.length - 1)) * 100;
      }

      return 0;
    };

    return (
      <StepperContainer progressPercentage={getProgressPercentage()}>
        {steps.map((step) => (
          <StepItem
            key={step.id}
            active={isStepActive(step.id)}
            completed={isStepCompleted(step.id)}
          >
            <div className="step-circle">
            </div>
            <div className="step-label">{step.label}</div>
          </StepItem>
        ))}
      </StepperContainer>
    );
  };

  return (
    <Grid container alignItems="center" mb={3}>
      <Grid item xs={12} md={6} mb={{ xs: 2, md: 0 }}>
        <Typography variant="h6" fontWeight="500" sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}>
          {currentStep === Step.REVIEW ? (
            <>
              {selectedMatchingRoundName}
            </>
          ) : currentStep === Step.EXPORT_SUMMARY ? (
            <>
              {selectedMatchingRoundName}
            </>
          ) : currentStep === Step.CONFIRM_EXPORT ? (
            <>
              <Box
                component="span"
                sx={{ mr: 1, fontSize: '20px', cursor: 'pointer' }}
                onClick={handleBack}
              >
                ←
              </Box>
              Confirm Export
            </>
          ) : currentStep === Step.EXPORT_LOG ? (
            <>
              <Box
                component="span"
                sx={{ mr: 1, fontSize: '20px', cursor: 'pointer' }}
                onClick={handleBack}
              >
                ←
              </Box>
              Export Summary
            </>
          ) : (
            'Select Matching Round'
          )}
        </Typography>
      </Grid>
      <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-start' } }}>
        {renderStepper()}
      </Grid>
    </Grid>
  );
};
